import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setSelectedView } from '../../../redux/specieProfileSlice';
import { menuRoutes } from "../../../router/menu";
import { UserApiResponse } from '../../../type/entities/user-type';
import { SpecieService } from '../../../services/specie/specieService';
import { RootState } from '../../../redux/store';
import {Button, Tooltip} from "rizzui";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.species.profile.info,
        permission: {
            group: 'species',
            action: 'get',
        },
    },
];

export default function SpeciesProfileLayout({ children, isLoading }: { children: React.ReactNode, isLoading?: boolean }) {

    // HOOKS 
    const { selectedView } = useSelector((state: RootState) => state.specieProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const service = new SpecieService();

    // STATES


    // DATA FETCHING

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getSpecieById(id);
        return response.getResponseData() as UserApiResponse;
    }, [id]));

const pageHeader = {
    title: (
        <>
            Especie{' '}
            <span className="italic">
                {data?.scientificName || 'Especie'}
            </span>
            {data?.commonName ? ` ${data.commonName}` : ''}
        </>
    ),
    breadcrumb: [
        { name: 'Administración' },
        { name: 'Especies' },
        { name: 'Perfil de la Especie' },
    ],
};

    // FUNCTIONS

    // ----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR ESPECIE
     * @EN DELETE SPECIE
     * @param id
     */
    // ----------------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteSpecie(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.species.path);
                toast.success('Se ha eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // ----------------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.species.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {userCan('delete', 'species') && (
                        <DeletePopover
                            title={`Eliminar Especie`}
                            description={`¿Estás seguro de que deseas eliminar el usuario?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-auto w-10 min-h-40'
                        />
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : children}
        </>
    );
}